import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { AlertCircle, Link01, Mail01 } from "untitledui-js-base";
import { handleCopy } from "utils/handleCopy";
import { AccountStatusVariant } from "views/rent-reporting/constants";
import { isPendingAccountStatus } from "views/rent-reporting/utils";
import { NotificationBanner } from "./NotificationBanner";
import { Subscriber } from "../types";

// The display status refines coarse `pending` into its sub-states (and under
// `combined_search_v2` the backend returns the refined status directly), so
// the pending banners key off it rather than raw verification/case fields.
export const PendingStatusBanners = ({
  renter,
  displayStatus,
  isResendingEmail,
  onResendEmail,
  onCopyVerificationLink
}: {
  renter: Subscriber;
  displayStatus: AccountStatusVariant;
  isResendingEmail: boolean;
  onResendEmail: (emailType: "opt_in" | "opt_out") => void;
  onCopyVerificationLink: () => void;
}) => {
  return (
    <>
      {isPendingAccountStatus(displayStatus) && renter.verification_link ? (
        <NotificationBanner
          icon={<AlertCircle size="24" className="stroke-danger-600" />}
          title={`${renter.name} needs to complete manual identity verification`}
          text={`${renter.name} will receive an email with instructions to
        complete manual verification. Alternatively, you can send the verification link directly to
        the renter.`}
          button={
            <Button
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.SM}
              Icon={Link01}
              onClick={onCopyVerificationLink}
            >
              Copy Verification Link
            </Button>
          }
        />
      ) : null}
      {displayStatus === AccountStatusVariant.PendingOptIn &&
      renter.opt_in_link ? (
        <NotificationBanner
          icon={<Mail01 size="24" className="stroke-content-100" />}
          title={`Opt-in email sent to ${renter.name}. They can opt-in at any time.`}
          text={`${renter.name} should receive an email with instructions to opt-in to rent reporting. Alternatively, you can send the opt-in link directly to the renter.`}
          button={
            <div className="flex gap-2">
              <Button
                variant={ButtonVariant.BASE}
                size={ButtonSize.SM}
                onClick={() => handleCopy(renter.opt_in_link || "")}
              >
                Copy link
              </Button>
              <Button
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.SM}
                loading={isResendingEmail}
                onClick={() => onResendEmail("opt_in")}
              >
                Resend email
              </Button>
            </div>
          }
        />
      ) : null}
      {displayStatus === AccountStatusVariant.PendingOptOut &&
      renter.opt_out_link &&
      (renter.remaining_opt_out_days ?? -1) >= 0 ? (
        <NotificationBanner
          icon={<Mail01 size="24" className="stroke-content-100" />}
          title={`Opt-out email sent to ${renter.name}. They have ${renter.remaining_opt_out_days} days to opt-out`}
          text={`${renter.name} should receive an email with instructions to opt-out of rent reporting. Alternatively, you can send the opt-out link directly to the renter.`}
          button={
            <div className="flex gap-2">
              <Button
                variant={ButtonVariant.BASE}
                size={ButtonSize.SM}
                onClick={() => handleCopy(renter.opt_out_link || "")}
              >
                Copy link
              </Button>
              <Button
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.SM}
                loading={isResendingEmail}
                onClick={() => onResendEmail("opt_out")}
              >
                Resend email
              </Button>
            </div>
          }
        />
      ) : null}
    </>
  );
};
